import {
  getPreferredScaleFromMessageThread,
  normalizeSupportedScale,
} from "../components/Chat/chatMessageChartState";

const CHART_RUNTIME_FIELDS = [
  "answeredTickerIds",
  "answeredTickerMap",
  "ticker",
  "tickerDescription",
  "logoUrls",
  "tickerPrices",
  "currentPrice",
  "currentPriceUpdatedAt",
  "currentPriceUpdatedAtIso",
  "closePrice",
  "priceChange1d",
  "pricePctChange1d",
  "pricePctChangeAh",
  "pricePctChange24hr",
  "assetType",
  "label1",
  "label2",
  "label3",
  "dates",
  "values1",
  "values2",
  "values3",
  "primaryLabel",
  "availableSecondaryKeys",
  "tradingScale",
  "workspaceChartBarsScale",
  "workspaceChartBarsTickerId",
];

const CHART_BAR_FIELDS = [
  "tickerPrices",
  "ticker_prices",
  "dates",
  "values1",
  "values_1",
  "values2",
  "values_2",
  "values3",
  "values_3",
  "workspaceChartBarsScale",
  "workspace_chart_bars_scale",
  "tickerPricesScale",
  "ticker_prices_scale",
  "workspaceChartBarsTickerId",
  "workspace_chart_bars_ticker_id",
  "tickerPricesTickerId",
  "ticker_prices_ticker_id",
];

const hasItems = (value) => Array.isArray(value) && value.length > 0;

const hasMeaningfulValue = (value) => {
  if (value == null) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "string") return value.trim().length > 0;
  return true;
};

const chartStateVisible = (message) => {
  const directState =
    message?.agenticChartState ||
    message?.agentic_chart_state ||
    null;
  const refinedState =
    message?.refinedQueryObj?.agentic_chart_state ||
    message?.refinedQueryObj?.agenticChartState ||
    null;
  const state = directState || refinedState;
  return state?.visible === true || state?.visible === "true";
};

const chartStateFrom = (message) =>
  message?.agenticChartState ||
  message?.agentic_chart_state ||
  message?.refinedQueryObj?.agentic_chart_state ||
  message?.refinedQueryObj?.agenticChartState ||
  null;

const primaryTickerId = (message) => {
  const chartState = chartStateFrom(message);
  const ids = Array.isArray(message?.answeredTickerIds)
    ? message.answeredTickerIds
    : Array.isArray(message?.answered_ticker_ids)
      ? message.answered_ticker_ids
      : [];
  const value = chartState?.primary_ticker_id ??
    chartState?.primaryTickerId ??
    ids.find((id) => id != null && id !== "") ??
    null;
  return value == null || value === "" ? null : String(value);
};

const embeddedBarsScale = (message) => {
  const explicit =
    message?.workspaceChartBarsScale ||
    message?.workspace_chart_bars_scale ||
    message?.tickerPricesScale ||
    message?.ticker_prices_scale ||
    null;
  return normalizeSupportedScale(explicit || "1d");
};

const chartBarCount = (message) => {
  const tickerPrices = message?.tickerPrices || message?.ticker_prices;
  if (Array.isArray(tickerPrices)) return tickerPrices.length;
  const valueSeries =
    message?.values2 || message?.values_2 ||
    message?.values1 || message?.values_1 || [];
  return Array.isArray(valueSeries) ? valueSeries.length : 0;
};

const clearOmittedStreamedBarFields = (mergedMessage, responseMessage) => {
  const responseTickerIds = Array.isArray(responseMessage?.answeredTickerIds)
    ? responseMessage.answeredTickerIds
    : responseMessage?.answered_ticker_ids;
  if (!hasItems(responseTickerIds)) return mergedMessage;

  let cleaned = mergedMessage;
  CHART_BAR_FIELDS.forEach((field) => {
    if (Object.prototype.hasOwnProperty.call(responseMessage, field)) return;
    if (!Object.prototype.hasOwnProperty.call(cleaned, field)) return;
    if (cleaned === mergedMessage) cleaned = { ...mergedMessage };
    delete cleaned[field];
  });
  return cleaned;
};

const preserveCompatibleStreamedBars = (
  mergedMessage,
  lastMessage,
  responseMessage,
) => {
  if (!chartStateVisible(responseMessage)) return mergedMessage;

  const streamedTickerId = primaryTickerId(lastMessage);
  const responseTickerId = primaryTickerId(responseMessage);
  if (
    !streamedTickerId ||
    !responseTickerId ||
    streamedTickerId !== responseTickerId
  ) {
    return mergedMessage;
  }

  const selectedScale = normalizeSupportedScale(
    getPreferredScaleFromMessageThread(responseMessage) || "1d",
  );
  const streamedScale = embeddedBarsScale(lastMessage);
  const responseScale = embeddedBarsScale(responseMessage);
  const streamedBarsAreCompatible =
    chartBarCount(lastMessage) >= 2 && streamedScale === selectedScale;
  const responseBarsAreCompatible =
    chartBarCount(responseMessage) >= 2 && responseScale === selectedScale;
  if (!streamedBarsAreCompatible || responseBarsAreCompatible) {
    return mergedMessage;
  }

  const chartFields = {};
  CHART_BAR_FIELDS.forEach((field) => {
    const value = lastMessage?.[field];
    if (hasMeaningfulValue(value)) chartFields[field] = value;
  });
  return { ...mergedMessage, ...chartFields };
};

const preserveStreamedChartFields = (mergedMessage, lastMessage, responseMessage) => {
  if (!hasItems(lastMessage?.answeredTickerIds)) return mergedMessage;
  if (hasItems(responseMessage?.answeredTickerIds)) return mergedMessage;
  if (!chartStateVisible(responseMessage)) return mergedMessage;

  const chartFields = {};
  CHART_RUNTIME_FIELDS.forEach((field) => {
    const value = lastMessage?.[field];
    if (hasMeaningfulValue(value)) chartFields[field] = value;
  });

  return {
    ...mergedMessage,
    ...chartFields,
  };
};

const getTradeProposal = (message) =>
  message?.refinedQueryObj?.trade_proposed ||
  message?.refinedQueryObj?.tradeProposed ||
  null;

// The merge spreads are shallow, and the streamed message may now carry a
// minimal refinedQueryObj holding only the socket-delivered trade_proposed.
// The HTTP refinedQueryObj is authoritative for everything else (scales,
// chart state, ...), so always base on it; the socket only ever contributes
// trade_proposed, re-attached when HTTP omitted it (e.g. a partial
// post-parse failure).
const reconcileRefinedQueryObj = (mergedMessage, lastMessage, responseMessage) => {
  let refinedQueryObj =
    responseMessage?.refinedQueryObj || mergedMessage.refinedQueryObj || null;

  const streamedProposal = getTradeProposal(lastMessage);
  if (streamedProposal && !getTradeProposal({ refinedQueryObj })) {
    refinedQueryObj = {
      ...(refinedQueryObj || {}),
      trade_proposed: streamedProposal,
    };
  }

  if (refinedQueryObj === mergedMessage.refinedQueryObj) return mergedMessage;
  return { ...mergedMessage, refinedQueryObj };
};

const preserveResponseEvidenceFields = (mergedMessage, responseMessage) => {
  const artifacts =
    responseMessage?.marketAnalysisArtifacts ||
    responseMessage?.market_analysis_artifacts ||
    null;
  if (!hasMeaningfulValue(artifacts)) return mergedMessage;
  return { ...mergedMessage, marketAnalysisArtifacts: artifacts };
};

const preserveStreamedRuntimeFields = (mergedMessage, lastMessage, responseMessage) =>
  preserveResponseEvidenceFields(
    reconcileRefinedQueryObj(
      preserveCompatibleStreamedBars(
        clearOmittedStreamedBarFields(
          preserveStreamedChartFields(mergedMessage, lastMessage, responseMessage),
          responseMessage,
        ),
        lastMessage,
        responseMessage,
      ),
      lastMessage,
      responseMessage,
    ),
    responseMessage,
  );

const completedFinalMessage = (message) => ({
  ...message,
  textStreamCompleted: true,
});

export const reconcileFinalBotMessage = (lastMessage, responseMessage) => {
  if (!responseMessage) return lastMessage;
  if (!lastMessage || lastMessage.sender !== "bot") return responseMessage;

  const preservedRuntimeFields = {};
  if (lastMessage.__stableKey) {
    preservedRuntimeFields.__stableKey = lastMessage.__stableKey;
  }
  if (lastMessage.__localKey) {
    preservedRuntimeFields.__localKey = lastMessage.__localKey;
  }

  const streamedText = typeof lastMessage.message === "string"
    ? lastMessage.message
    : "";
  const finalText = typeof responseMessage.message === "string"
    ? responseMessage.message
    : "";

  // A post-answer failure (e.g. an OpenAI timeout during follow-up/ticker
  // extraction) returns an error body even though the full answer already
  // streamed in over the WS. Keep the streamed answer and its chart rather than
  // letting the error string overwrite them; only surface the error when nothing
  // was streamed.
  if (responseMessage.isError) {
    return streamedText ? completedFinalMessage(lastMessage) : responseMessage;
  }

  if (streamedText === finalText) {
    return completedFinalMessage(preserveStreamedRuntimeFields({
      ...lastMessage,
      ...responseMessage,
      ...preservedRuntimeFields,
      message: streamedText,
    }, lastMessage, responseMessage));
  }

  if (streamedText && finalText.startsWith(streamedText)) {
    return completedFinalMessage(preserveStreamedRuntimeFields({
      ...lastMessage,
      ...responseMessage,
      ...preservedRuntimeFields,
      message: finalText,
    }, lastMessage, responseMessage));
  }

  if (finalText && streamedText.startsWith(finalText)) {
    return completedFinalMessage(preserveStreamedRuntimeFields({
      ...responseMessage,
      ...lastMessage,
      ...preservedRuntimeFields,
      message: streamedText,
    }, lastMessage, responseMessage));
  }

  return completedFinalMessage(preserveStreamedRuntimeFields({
    ...lastMessage,
    ...responseMessage,
    ...preservedRuntimeFields,
  }, lastMessage, responseMessage));
};
